<script setup lang="ts">
import device from '@@/bits/device'
import { defineAsyncComponent } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Id } from '@@/types'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { GroupByTypes } from '@padlet/arvo'
import { storeToRefs } from 'pinia'

const SurfaceAddSectionButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddSectionButton.vue'))
const SurfacePostsTimeline = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostsTimeline.vue'))

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

defineEmits(['scroll'])

const { xHeader, backgroundLuminance, isReadOnly, isScreenshotMode, isFrozen } = storeToRefs(useSurfaceStore())
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const { isViewingTranslatedPadlet } = storeToRefs(useSurfaceTranslationsStore())
if (!device.mobile) useDoubleClickToPost()
</script>

<template>
  <div
    id="wall-container"
    :class="{
      'scrollbar-photo': backgroundLuminance === 'light',
      'scrollbar-photo-dark': backgroundLuminance === 'dark',
      'overflow-x-hidden': !isReadOnly,
      'w-full h-full': isScreenshotMode,
    }"
    @scroll="$emit('scroll', $event)"
  >
    <SurfaceHeader v-if="xHeader" />

    <div class="flex flex-col grow justify-center timeline-sections-and-add-button-wrapper">
      <SurfacePostsTimeline
        :reaction-ids-by-post-id="reactionIdsByPostId"
        :comment-ids-by-post-id="commentIdsByPostId"
      />
      <!-- Need self-start so this div won't take up the full width of the parent and block double click to post -->
      <div
        v-if="
          wishGroupBy === GroupByTypes.Section && canIModerate && !isReadOnly && !isFrozen && !isViewingTranslatedPadlet
        "
        class="flex flex-col items-center self-start mt-4 pb-16 ps-4 tablet-portrait:ps-6"
      >
        <div v-if="!isDemoPadletPanelDesktop" class="w-full">
          <SurfaceAddSectionButton class="ms-4 tablet-portrait:ms-0" />
        </div>
      </div>
    </div>
  </div>
</template>
